import React, { useState, useEffect, useRef, useCallback } from 'react';
import { productsAPI, authAPI, alertsAPI } from '../api/client';
import ProductCard from '../components/ProductCard';
import ProductModal from '../components/ProductModal';
import RestockAlertModal from '../components/RestockAlertModal';
import Filters from '../components/Filters';
import Header from '../components/Header';
import UserSelectionModal from '../components/UserSelectionModal';
import { useAuthStore } from '../store';
import { formatKyivDate } from '../utils/kyivTime';
import '../styles/home.css';

const PAGE_SIZE = 96;

// Глобальные счётчики — отменяют устаревшие ответы
let resetGeneration = 0;
let brandsGeneration = 0;

const HomePage = ({ onLogout, onAdmin, onSettings, onSwitchSection, canSwitchSection }) => {
  const [products, setProducts] = useState([]);
  const [brandsModels, setBrandsModels] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem('catalog_filters');
      if (saved) return { search: '', brand: '', model: '', sizes: [], allSizes: false, withSizes: false, minSupplierPrice: '', maxSupplierPrice: '', newItems: false, ...JSON.parse(saved) };
    } catch {}
    return { search: '', brand: '', model: '', sizes: [], allSizes: false, withSizes: false, minSupplierPrice: '', maxSupplierPrice: '', newItems: false };
  });
  const [sizes, setSizes] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [adminBadge, setAdminBadge] = useState(0);
  const [directProduct, setDirectProduct] = useState(null); // товар открытый по прямой ссылке
  const [userAlerts, setUserAlerts] = useState([]);
  const [alertProduct, setAlertProduct] = useState(null);
  const [showAlertsList, setShowAlertsList] = useState(false);
  const [showSelection, setShowSelection] = useState(false);
  const [selectionInitialFilters, setSelectionInitialFilters] = useState(null);

  const currentUser = useAuthStore((state) => state.user);

  const filtersRef = useRef(filters);
  const loadProductsRef = useRef(null);
  const skipRef = useRef(0);
  const hasMoreRef = useRef(true);
  const isLoadingRef = useRef(false);
  const isFirstRender = useRef(true);
  const sentinelRef = useRef(null);
  const currentGenRef = useRef(0); // поколение текущего reset-запроса
  const loadingSkipRef = useRef(-1); // skip значення яке зараз вантажиться (захист від дублів)

  const loadAlerts = useCallback(async () => {
    try {
      const res = await alertsAPI.list();
      setUserAlerts(res.data || []);
    } catch {}
  }, []);

  const loadAdminBadge = useCallback(async () => {
    if (!currentUser?.is_admin) return;
    try {
      const [usersRes, catRes, supRes] = await Promise.allSettled([
        authAPI.getUsers(),
        productsAPI.getSyncStatus(),
        productsAPI.getSuppliersStatus(),
      ]);
      let count = 0;
      if (usersRes.status === 'fulfilled') {
        count += usersRes.value.data.filter(u => !u.is_active && !u.is_admin && !u.is_rejected).length;
      }
      if (catRes.status === 'fulfilled' && catRes.value.data?.last_error) count++;
      if (supRes.status === 'fulfilled' && supRes.value.data?.last_error) count++;
      setAdminBadge(count);
    } catch {}
  }, [currentUser?.is_admin]);

  const loadBrandsModels = useCallback((attempt = 0) => {
    const { search, sizes, allSizes, withSizes, minSupplierPrice, maxSupplierPrice, newItems, season } = filtersRef.current;
    brandsGeneration += 1;
    const myGen = brandsGeneration;
    productsAPI.getBrandsModels(search, sizes, allSizes, withSizes, minSupplierPrice, maxSupplierPrice, newItems, season)
      .then(r => {
        if (brandsGeneration !== myGen) return; // устаревший ответ — игнорируем
        setBrandsModels(r.data);
      })
      .catch(e => {
        console.error('Ошибка загрузки брендов:', e);
        if (attempt < 3) setTimeout(() => loadBrandsModels(attempt + 1), 5000);
      });
  }, []);

  const loadProducts = useCallback(async (reset = false) => {
    if (!reset && isLoadingRef.current) return;
    if (!reset && !hasMoreRef.current) return;
    // Захист від дублювання: не вантажимо один і той самий skip двічі
    if (!reset && loadingSkipRef.current === skipRef.current) return;

    isLoadingRef.current = true;

    if (reset) {
      // Инкрементируем поколение — все запросы предыдущего поколения будут проигнорированы
      resetGeneration += 1;
      currentGenRef.current = resetGeneration;
      skipRef.current = 0;
      hasMoreRef.current = true;
      loadingSkipRef.current = -1;
      setLoading(true);
      setLoadingMore(false);
    } else {
      setLoadingMore(true);
    }

    const myGen = currentGenRef.current;
    const skip = skipRef.current;
    loadingSkipRef.current = skip;

    try {
      const response = await productsAPI.listProducts(skip, PAGE_SIZE, filtersRef.current);

      // Если за время запроса пришёл новый reset — игнорируем результат
      if (currentGenRef.current !== myGen) return;

      const newProducts = response.data;

      if (reset) {
        setProducts(newProducts);
      } else {
        // Дедуплікація на випадок race condition
        setProducts(prev => {
          const existingSkus = new Set(prev.map(p => p.sku));
          const unique = newProducts.filter(p => !existingSkus.has(p.sku));
          return unique.length > 0 ? [...prev, ...unique] : prev;
        });
      }

      setLoadError(false);
      skipRef.current = skip + newProducts.length;
      hasMoreRef.current = newProducts.length === PAGE_SIZE && !filtersRef.current.newItems;
    } catch (error) {
      if (currentGenRef.current !== myGen) return;
      console.error('Ошибка загрузки товаров:', error);
      setLoadError(true);
      loadingSkipRef.current = -1;
      // Автоповтор через 10 секунд если сервис недоступен
      const retryGen = currentGenRef.current;
      setTimeout(() => {
        if (currentGenRef.current === retryGen) loadProductsRef.current(true);
      }, 10000);
    } finally {
      if (currentGenRef.current === myGen) {
        isLoadingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  loadProductsRef.current = loadProducts;

  useEffect(() => {
    loadBrandsModels();
    loadProducts(true);
    loadAdminBadge();
    loadAlerts();
    const loadSizes = (attempt = 0) => {
      productsAPI.getSizes()
        .then(r => setSizes(r.data))
        .catch(() => { if (attempt < 3) setTimeout(() => loadSizes(attempt + 1), 5000); });
    };
    loadSizes();

    // Открываем товар по прямой ссылке ?sku= или после обновления страницы
    const params = new URLSearchParams(window.location.search);
    const skuFromUrl = params.get('sku');
    const skuToOpen = skuFromUrl || sessionStorage.getItem('open_product_sku');
    if (skuToOpen) {
      if (skuFromUrl) window.history.replaceState({}, '', window.location.pathname);
      productsAPI.getBySku(skuToOpen)
        .then(r => { if (r.data) setDirectProduct(r.data); })
        .catch(() => {});
    }
  }, [loadProducts, loadBrandsModels, loadAdminBadge, loadAlerts]);

  useEffect(() => {
    if (!currentUser?.is_admin) return;
    const interval = setInterval(loadAdminBadge, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadAdminBadge, currentUser?.is_admin]);

  // Сохраняем позицию скролла при уходе со страницы
  useEffect(() => {
    const save = () => sessionStorage.setItem('catalog_scroll', String(window.scrollY));
    window.addEventListener('beforeunload', save);
    return () => { save(); window.removeEventListener('beforeunload', save); };
  }, []);

  // Восстанавливаем скролл после первой загрузки товаров
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (loading || scrollRestoredRef.current) return;
    scrollRestoredRef.current = true;
    const saved = sessionStorage.getItem('catalog_scroll');
    if (saved) {
      sessionStorage.removeItem('catalog_scroll');
      setTimeout(() => window.scrollTo(0, parseInt(saved, 10)), 50);
    }
  }, [loading]);

  // Тихое обновление брендов каждые 5 минут (без сброса товаров и скролла)
  useEffect(() => {
    const interval = setInterval(loadBrandsModels, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadBrandsModels]);

  // Debounced search — обновляем и товары, и дерево фильтров
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      loadProducts(true);
      loadBrandsModels();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadProducts(false);
        }
      },
      { rootMargin: '800px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadProducts]);

  // После сброса фильтров 48 товаров могут не заполнить экран —
  // sentinel остаётся видимым, но observer не перезапускается.
  // Проверяем вручную каждый раз когда loading переходит в false.
  useEffect(() => {
    if (loading) return;
    if (!hasMoreRef.current || isLoadingRef.current) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const { top } = sentinel.getBoundingClientRect();
    if (top < window.innerHeight + 300) {
      loadProducts(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const saveFilters = (f) => { try { sessionStorage.setItem('catalog_filters', JSON.stringify(f)); } catch {} };

  const DEFAULT_FILTERS = { search: '', brand: '', model: '', season: '', sizes: [], allSizes: false, withSizes: false, minSupplierPrice: '', maxSupplierPrice: '', newItems: false };

  const hasActiveFilters = filters.newItems || filters.search || filters.brand || filters.model || filters.season ||
    filters.sizes?.length > 0 || filters.withSizes || filters.minSupplierPrice || filters.maxSupplierPrice;

  const handleResetFilters = () => {
    filtersRef.current = DEFAULT_FILTERS;
    setFilters(DEFAULT_FILTERS);
    saveFilters(DEFAULT_FILTERS);
    loadProducts(true);
    loadBrandsModels();
  };

  // Сохраняем SKU открытого товара чтобы восстановить после обновления страницы
  useEffect(() => {
    const sku = directProduct?.sku ?? (selectedIndex !== null ? products[selectedIndex]?.sku : null);
    if (sku) sessionStorage.setItem('open_product_sku', sku);
    else sessionStorage.removeItem('open_product_sku');
  }, [directProduct, selectedIndex, products]);

  const handleSearchChange = (search) => {
    const newFilters = { ...filtersRef.current, search };
    filtersRef.current = newFilters;
    setFilters(newFilters);
    saveFilters(newFilters);
  };

  const handleFilterChange = (newFilters) => {
    filtersRef.current = newFilters;
    setFilters(newFilters);
    saveFilters(newFilters);
    loadProducts(true);
    loadBrandsModels();
  };

  const alertSkus = new Set(userAlerts.map(a => a.product_sku));

  const activeFilterCount = (filters.brand ? 1 : 0) + (filters.model ? 1 : 0) + (filters.season ? 1 : 0) +
    filters.sizes.length + (filters.allSizes ? 1 : 0) + (filters.withSizes ? 1 : 0) +
    (filters.minSupplierPrice ? 1 : 0) + (filters.maxSupplierPrice ? 1 : 0) +
    (filters.newItems ? 1 : 0);


  return (
    <div className="home-page">
      <Header
        onLogout={onLogout}
        onAdmin={() => { onAdmin(); setAdminBadge(0); }}
        adminBadgeCount={adminBadge}
        onSettings={onSettings}
        onFilterToggle={() => setFiltersOpen(o => !o)}
        activeFilterCount={activeFilterCount}
        search={filters.search}
        onSearchChange={handleSearchChange}
        onSearch={() => loadProducts(true)}
        sizes={sizes}
        selectedSizes={filters.sizes}
        allSizes={filters.allSizes}
        withSizes={filters.withSizes}
        onSizeToggle={(size) => {
          const cur = filtersRef.current.sizes;
          const next = cur.includes(size) ? cur.filter(s => s !== size) : [...cur, size];
          const newFilters = { ...filtersRef.current, sizes: next };
          filtersRef.current = newFilters;
          setFilters(newFilters);
          saveFilters(newFilters);
          loadProducts(true);
          loadBrandsModels();
        }}
        onAllSizesToggle={() => {
          const newFilters = { ...filtersRef.current, allSizes: !filtersRef.current.allSizes };
          filtersRef.current = newFilters;
          setFilters(newFilters);
          saveFilters(newFilters);
          loadProducts(true);
          loadBrandsModels();
        }}
        onWithSizesToggle={() => {
          const newFilters = { ...filtersRef.current, withSizes: !filtersRef.current.withSizes };
          filtersRef.current = newFilters;
          setFilters(newFilters);
          saveFilters(newFilters);
          loadProducts(true);
          loadBrandsModels();
        }}
        onSizesClear={() => {
          const newFilters = { ...filtersRef.current, sizes: [], allSizes: false, withSizes: false };
          filtersRef.current = newFilters;
          setFilters(newFilters);
          saveFilters(newFilters);
          loadProducts(true);
          loadBrandsModels();
        }}
        alertsCount={userAlerts.length}
        onAlerts={() => setShowAlertsList(true)}
        currentFilters={filters}
        onSelection={(initialFilters) => { setSelectionInitialFilters(initialFilters || null); setShowSelection(true); }}
        onSwitchSection={onSwitchSection}
        canSwitchSection={canSwitchSection}
      />

      <main className="main-content">
        <div className="products-section">
          <div className="section-header">
            <h2 className="section-title">
              {filters.newItems ? 'Новинки' : 'Каталог товаров'}
            </h2>
          </div>

          {loading && (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          )}

          {!loading && products.length === 0 && (
            <div className="no-results">
              {loadError ? (
                <>
                  <p>Не удалось загрузить товары. Подключаемся...</p>
                  <p style={{ fontSize: '0.85em', opacity: 0.6 }}>Повтор через 10 секунд</p>
                </>
              ) : (
                <>
                  <p>Товары не найдены. Попробуйте изменить фильтры.</p>
                  {hasActiveFilters && (
                    <button className="reset-filters-btn" onClick={handleResetFilters}>
                      Сбросить фильтры
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          <div className="products-grid">
            {products.map((product, idx) => (
              <ProductCard
                key={product.id}
                product={product}
                onSelect={() => setSelectedIndex(idx)}
                hasAlert={alertSkus.has(product.sku)}
                onAlert={() => setAlertProduct(product)}
              />
            ))}
          </div>

          <div ref={sentinelRef} className="scroll-sentinel" />

          {loadingMore && (
            <div className="loading-more">
              <div className="spinner"></div>
            </div>
          )}

          {!loadingMore && !hasMoreRef.current && products.length > 0 && (
            <div className="all-loaded">Все товары загружены ({products.length})</div>
          )}
        </div>
      </main>

      {/* Desktop sidebar */}
      <aside className="catalog-sidebar-fixed">
        <Filters
          brandsModels={brandsModels}
          filters={filters}
          onFilterChange={handleFilterChange}
        />
      </aside>

      {/* Mobile filter drawer */}
      {filtersOpen && (
        <div className="filter-drawer-overlay" onClick={() => setFiltersOpen(false)}>
          <div className="filter-drawer" onClick={e => e.stopPropagation()}>
            <div className="filter-drawer-handle" />
            <Filters
              brandsModels={brandsModels}
              filters={filters}
              onFilterChange={(f) => { handleFilterChange(f); }}
              onApply={() => setFiltersOpen(false)}
            />
          </div>
        </div>
      )}

      {directProduct && (
        <ProductModal
          product={directProduct}
          onClose={() => { setDirectProduct(null); sessionStorage.removeItem('open_product_sku'); }}
          onPrev={null}
          onNext={null}
          hasAlert={alertSkus.has(directProduct.sku)}
          onAlert={() => setAlertProduct(directProduct)}
        />
      )}

      {!directProduct && selectedIndex !== null && products[selectedIndex] && (
        <ProductModal
          product={products[selectedIndex]}
          onClose={() => { setSelectedIndex(null); sessionStorage.removeItem('open_product_sku'); }}
          onPrev={selectedIndex > 0 ? () => setSelectedIndex(i => i - 1) : null}
          onNext={selectedIndex < products.length - 1 ? () => setSelectedIndex(i => i + 1) : null}
          hasAlert={alertSkus.has(products[selectedIndex].sku)}
          onAlert={() => setAlertProduct(products[selectedIndex])}
        />
      )}

      {alertProduct && (
        <RestockAlertModal
          product={alertProduct}
          onClose={() => setAlertProduct(null)}
          existingAlert={userAlerts.find(a => a.product_sku === alertProduct.sku) || null}
          onAlertChange={loadAlerts}
        />
      )}

      {showAlertsList && (
        <AlertsListModal
          alerts={userAlerts}
          onClose={() => setShowAlertsList(false)}
          onDelete={async (id) => {
            await alertsAPI.delete(id).catch(console.error);
            await loadAlerts();
          }}
        />
      )}

      {showSelection && (
        <UserSelectionModal
          onClose={() => { setShowSelection(false); setSelectionInitialFilters(null); }}
          initialFilters={selectionInitialFilters}
        />
      )}
    </div>
  );
};

const AlertsListModal = ({ alerts, onClose, onDelete }) => {
  const [deleting, setDeleting] = useState(null);
  const fmt = (iso) => iso ? formatKyivDate(iso) : '';
  return (
    <div className="alert-overlay" onClick={onClose}>
      <div className="alerts-list-modal" onClick={e => e.stopPropagation()}>
        <div className="alert-header">
          <span>Мои подписки ({alerts.length})</span>
          <button className="alert-close" onClick={onClose}>×</button>
        </div>
        <div className="alerts-list-body">
          {alerts.length === 0 ? (
            <div className="alerts-list-empty">Активных подписок нет</div>
          ) : alerts.map(a => (
            <div key={a.id} className="alert-item">
              <div className="alert-item-info">
                <div className="alert-item-sku">{a.product_sku}</div>
                <div className="alert-item-size">Размер {a.size}</div>
                {a.contact && <div className="alert-item-contact">{a.contact}</div>}
              </div>
              <div className="alert-item-date">{fmt(a.created_at)}</div>
              <button
                className="alert-item-del"
                disabled={deleting === a.id}
                title="Отменить подписку"
                onClick={async () => {
                  setDeleting(a.id);
                  await onDelete(a.id);
                  setDeleting(null);
                }}
              >×</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
